import { memo, useCallback, useMemo, useRef } from 'react';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useDynamicFieldContext } from '../DynamicFieldContext';
import { useTranslation } from 'react-i18next';
import { getFieldLabel } from '../utility/functions';

export type SearchFieldProps = {
  disabled?: boolean;
};

export const SearchField = memo(({ disabled = false }: SearchFieldProps) => {
  const { t } = useTranslation();
  const { fieldConfig, searchText, updateSearchText } = useDynamicFieldContext();
  const textInputRef = useRef<HTMLInputElement>(null);
  const strSearch = t('blox.general.search');

  // Clean string label for mui outlined
  const fieldLabel = useMemo(() => {
    return getFieldLabel(fieldConfig);
  }, [fieldConfig]);

  const fieldName = useMemo(() => {
    return fieldConfig?.name ?? '';
  }, [fieldConfig?.name]);

  const testId = useMemo(() => {
    return `dynamic-field-${fieldName}-search-input`;
  }, [fieldName]);

  const searchPlaceholderTxt = `${strSearch} ${fieldLabel}...`;

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateSearchText(e.target.value);
    },
    [updateSearchText],
  );

  const handleClearInput = useCallback(() => {
    updateSearchText('');
    textInputRef?.current?.focus();
  }, [updateSearchText]);

  return (
    <TextField
      fullWidth
      size="small"
      variant="outlined"
      placeholder={searchPlaceholderTxt}
      value={searchText}
      onChange={handleInputChange}
      inputRef={textInputRef}
      disabled={disabled}
      slotProps={{
        input: {
          endAdornment: searchText && (
            <InputAdornment position="end">
              <IconButton
                aria-label="clear"
                onClick={handleClearInput}
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        },

        htmlInput: {
          'data-testid': testId,
        },
      }}
    />
  );
});
SearchField.displayName = 'SearchField';
